import {
  CustomFieldConfig,
  DisplayCondition,
  OnboardingApplication,
} from "@dreambigger/shared/src/types";
import { Conditional } from "@dreambigger/conditional";
import { isInAdminMode } from "../../../components/adminApplicationStore";

/**
 * Resolve the user's `authenticationMode` for display-condition evaluation.
 * Today this is binary — `"admin"` when the admin-edit lifecycle has mounted
 * `AdminApplicationWrapper`, `undefined` otherwise. Mirrors how the
 * server-side product-visibility evaluator surfaces `authenticationMode`,
 * so display-condition writers can use the same `{"authenticationMode":
 * ["admin", "$eq"]}` shape they already use on products.
 */
const currentAuthenticationMode = (): string | undefined =>
  isInAdminMode() ? "admin" : undefined;

function evaluateDisplayCondition(
  condition: DisplayCondition,
  formValues: Record<string, unknown>,
  application?: OnboardingApplication,
  currentSlug?: string
): boolean {
  const useOtherStep = condition.slug && condition.slug !== currentSlug;
  let data: Record<string, unknown> = formValues;
  if (useOtherStep) {
    const response = application?.responses.find(
      (r) => r.slug === condition.slug
    );
    if (!response) return false;
    data = response.fields;
  }

  // `field` is optional — conditions can gate purely on authenticationMode,
  // utmParam, or brandVariantId without a form-field reference. Treat
  // missing `field` as "this part of the condition does not apply" (i.e.
  // passes), same way an absent utmParam or brandVariantId passes.
  const fieldEvaluation = condition.field
    ? Conditional.evaluate({
        conditions: { [condition.field.key]: condition.field.value },
        data,
      })
    : true;

  const utmParam = condition.utmParam;
  const utmParams = application?.utmParams;
  const utmEvaluation =
    utmParams && utmParam?.key && utmParam.value
      ? Conditional.evaluate({
          conditions: { [utmParam.key]: utmParam.value },
          data: utmParams,
        })
      : true;

  const brandVariantEvaluation = condition.brandVariantId
    ? Conditional.evaluate({
        conditions: { brandVariantId: condition.brandVariantId },
        data: { brandVariantId: application?.brandVariantId },
      })
    : true;

  const authModeEvaluation =
    condition.authenticationMode !== undefined
      ? Conditional.evaluate({
          conditions: { authenticationMode: condition.authenticationMode },
          data: { authenticationMode: currentAuthenticationMode() },
        })
      : true;

  return (
    fieldEvaluation &&
    utmEvaluation &&
    brandVariantEvaluation &&
    authModeEvaluation
  );
}

/**
 * Check if a field should be displayed based on its display conditions.
 *
 * Supports transitive evaluation: when a display condition references a local
 * field (no slug), we first check if that dependency field is itself visible.
 * This prevents hidden fields (e.g. BO checkbox hidden by cross-step conditions)
 * from causing dependent fields to incorrectly show.
 */
export default function shouldDisplayField(
  field: CustomFieldConfig,
  formValues: Record<string, unknown>,
  application?: OnboardingApplication,
  currentSlug?: string,
  allFields?: CustomFieldConfig[],
  visited: ReadonlySet<string> = new Set()
): boolean {
  if (!field.display || field.display.length === 0) return true;
  // Cycle guard: if this field is already being evaluated higher up the chain,
  // a dependent field references itself (directly or transitively). Return true
  // rather than false because hiding fields would silently prevent users from
  // editing data they need to submit.
  if (visited.has(field.fieldKey)) return true;
  const nextVisited = new Set(visited);
  nextVisited.add(field.fieldKey);

  return field.display.some((condition) => {
    if (Array.isArray(condition)) {
      return condition.every((c) => {
        // Dependency walk only applies when the condition references a
        // form `field`. Conditions that gate purely on non-form sources
        // (authenticationMode, utmParam, brandVariantId) have no
        // local-field dependency to follow.
        if (!c.slug && c.field && allFields) {
          const dep = allFields.find((f) => f.fieldKey === c.field?.key);
          if (
            dep?.display?.length &&
            !shouldDisplayField(
              dep,
              formValues,
              application,
              currentSlug,
              allFields,
              nextVisited
            )
          )
            return false;
        }
        return evaluateDisplayCondition(
          c,
          formValues,
          application,
          currentSlug
        );
      });
    }
    const c = condition as DisplayCondition;
    if (!c.slug && c.field && allFields) {
      const dep = allFields.find((f) => f.fieldKey === c.field?.key);
      if (
        dep?.display?.length &&
        !shouldDisplayField(
          dep,
          formValues,
          application,
          currentSlug,
          allFields,
          nextVisited
        )
      )
        return false;
    }
    return evaluateDisplayCondition(c, formValues, application, currentSlug);
  });
}
